import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	OnDestroy
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { ISearchSymbol, SearchCategory } from '../../models/search-symbol.model';
import { SearchSymbolService } from '../../services/search-symbol.service';
import { convertPersianNumbersToEnglish, convertToPersianText } from '../../../../tools/primptives-tool/string-tool';
import { timeout } from '../../../../tools/event-tool';
import { NgIf } from '@angular/common';

@Component({
	standalone: true,
	selector: 'search-symbol-input',
	templateUrl: './search-symbol-input.component.html',
	imports: [NgIf, FormsModule, ReactiveFormsModule]
})
export class SearchSymbolInputComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('searchInput') searchInput: ElementRef;
	@Output() valueChanged: EventEmitter<string> = new EventEmitter<string>();
	@Output() clearClicked = new EventEmitter();
	@Output() resultGenerated: EventEmitter<ISearchSymbol[]> = new EventEmitter<ISearchSymbol[]>();
	@Output() resultGeneratedCategorySplited = new EventEmitter<Map<SearchCategory, ISearchSymbol[]>>();
	@Output() arrowDown = new EventEmitter();
	@Output() arrowUp = new EventEmitter();
	@Output() enterPressed = new EventEmitter();
	@Input() placeHolder = 'جستجوی نماد';
	@Input() selectedSymbolName = '';
	@Input() isMobile = true;
	@Input() showNothingFoundText = true;
	@Input() searchByCategorySplited = false;
	@Input() includeFunds = false;
	@Input() includeCars = false;
	@Input() showDefaults = false;

	spanStates = {
		loading: ' mdi-spin mdi-loading ',
		close: ' mdi-close-circle '
	};
	spanStateClass = this.spanStates.close;
	searchInputControl: FormControl = new FormControl();
	searchResult: ISearchSymbol[] = [];
	searchResultByCategorySplited = new Map<SearchCategory, ISearchSymbol[]>();
	subInputChange: Subscription;
	showClearButton = false;
	stockIsSelected = false;

	constructor(private searchService: SearchSymbolService) {}

	@Input() set clear(value: boolean) {
		if (value) {
			this.onClearSearchClicked();
		}
	}

	ngOnInit() {
		this.applyInputSubscription();
	}

	ngOnDestroy() {
		this.removeInputSubscription();
	}

	applyInputSubscription() {
		this.subInputChange = this.searchInputControl.valueChanges
			.pipe(
				tap((value: string) => {
					if (value?.length === 0) {
						this.searchResult = [];
						this.searchResultByCategorySplited = new Map<SearchCategory, ISearchSymbol[]>();
						if (this.showDefaults) {
							if (this.searchByCategorySplited) {
								this.resultGeneratedCategorySplited.emit(
									this.searchService.defaultSearchSymbolsWithCategory
								);
							} else {
								this.resultGenerated.emit(this.searchService.defaultSearchSymbols);
							}

							this.spanStateClass = this.spanStates.close;
							return;
						}
						if (this.searchByCategorySplited) {
							this.resultGeneratedCategorySplited.emit(this.searchResultByCategorySplited);
						} else {
							this.resultGenerated.emit(this.searchResult);
						}
						this.spanStateClass = this.spanStates.close;
					}
					this.valueChanged.emit(value);
					this.spanStateClass = this.spanStates.loading;
					this.showClearButton = value?.length > 0;
				}),
				map((value: string) => {
					if (value && !this.searchService.isTextSymbolIsin(value)) {
						let persianVal = convertToPersianText(value, true);
						persianVal = convertPersianNumbersToEnglish(persianVal);
						if (value !== persianVal)
							// very important for avoiding infinine loop!
							this.searchInputControl.setValue(persianVal);
						return persianVal;
					}
					return value;
				}),
				debounceTime(500)
			)
			.subscribe((value: string) => {
				this.onSearchChange(value);
			});
	}

	removeInputSubscription() {
		this.subInputChange.unsubscribe();
	}

	setInputSearchValue(value: string) {
		this.subInputChange?.unsubscribe();
		this.searchInputControl.setValue(value);
		this.showClearButton = value?.length > 0;
		this.onSearchChange(value);
		this.applyInputSubscription();
	}

	ngAfterViewInit() {
		if (!this.isMobile) this.searchInput.nativeElement.focus();
		else timeout(() => this.searchInput.nativeElement.blur(), 1);
		if (this.showDefaults) {
			this.resultGenerated.emit(this.searchService.defaultSearchSymbols);
			this.resultGeneratedCategorySplited.emit(this.searchService.defaultSearchSymbolsWithCategory);
		}
	}

	onSearchChange(value: string) {
		if (this.stockIsSelected) {
			this.stockIsSelected = false;
			this.spanStateClass = this.spanStates.close;
			return;
		}
		if (!value) {
			this.searchResult = [];
			this.searchResultByCategorySplited = new Map<SearchCategory, ISearchSymbol[]>();
			if (this.showDefaults) {
				if (this.searchByCategorySplited) {
					this.resultGeneratedCategorySplited.emit(this.searchService.defaultSearchSymbolsWithCategory);
				} else {
					this.resultGenerated.emit(this.searchService.defaultSearchSymbols);
				}

				this.spanStateClass = this.spanStates.close;
				return;
			}
			if (this.searchByCategorySplited) {
				this.resultGeneratedCategorySplited.emit(this.searchResultByCategorySplited);
			} else {
				this.resultGenerated.emit(this.searchResult);
			}
			this.spanStateClass = this.spanStates.close;
			return;
		}

		if (this.searchService.isTextSymbolIsin(value)) {
			if (this.searchByCategorySplited) {
				this.searchResultByCategorySplited = this.searchService.searchBySymbolIsinCategorySplited(value);
			} else {
				this.searchResult = this.searchService.searchBySymbolIsin(value);
			}
		} else {
			if (this.searchByCategorySplited) {
				this.searchResultByCategorySplited = this.searchService.searchStocksByCategoryResult(
					value,
					0,
					100,
					this.includeFunds,
					this.includeCars
				);
			} else {
				this.searchResult = this.searchService.searchStocks(value, 0, 100, this.includeFunds, this.includeCars);
			}
		}
		this.resultGenerated.emit(this.searchResult);
		this.resultGeneratedCategorySplited.emit(this.searchResultByCategorySplited);
		this.spanStateClass = this.spanStates.close;
	}

	onClearSearchClicked() {
		this.onClearText();
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		this.searchInput?.nativeElement.focus();
		this.clearClicked.emit();
	}

	onClearText() {
		this.showClearButton = false;
		this.searchInputControl.setValue('');
		// this.searchResult = [];
		// this.searchResultByCategorySplited = new Map<SearchCategory, ISearchSymbol[]>();
		// this.resultGenerated.emit(this.searchResult);
		// this.resultGeneratedCategorySplited.emit(this.searchResultByCategorySplited);
	}

	arrowup() {
		this.arrowUp.emit();
	}

	arrowdown() {
		this.arrowDown.emit();
	}

	enter(e) {
		this.enterPressed.emit(e);
	}
}
